import React, { useRef, useCallback } from 'react';
import { useSessionStore } from '../store/sessionStore';
import './ReplayScrubber.css';

export function ReplayScrubber() {
  const { events, replayPosition, setReplayPosition, isReplaying, setIsReplaying, replaySpeed, setReplaySpeed, anomalies } = useSessionStore();
  const trackRef = useRef<HTMLDivElement>(null);
  const playbackTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  if (events.length === 0) {
    return (
      <div className="replay-scrubber-empty">
        No replay data available
      </div>
    );
  }

  const sessionStart = events[0]?.timestamp_ms || Date.now();
  const sessionEnd = events[events.length - 1]?.timestamp_ms || Date.now();
  const duration = sessionEnd - sessionStart || 1;

  const position = (replayPosition - sessionStart) / duration;

  function positionToMs(clientX: number): number {
    const track = trackRef.current;
    if (!track) return sessionStart;
    const rect = track.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return sessionStart + ratio * duration;
  }

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setReplayPosition(positionToMs(e.clientX));

      function onMove(me: MouseEvent) {
        setReplayPosition(positionToMs(me.clientX));
      }
      function onUp() {
        setIsReplaying(false);
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      }
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [sessionStart, duration, setReplayPosition, setIsReplaying]
  );

  function handlePlay() {
    if (!isReplaying) {
      setIsReplaying(true);
      const stepMs = 100;
      playbackTimer.current = setInterval(() => {
        const { replayPosition: pos } = useSessionStore.getState();
        const newPos = pos + stepMs * replaySpeed;
        if (newPos >= sessionEnd) {
          setIsReplaying(false);
          setReplayPosition(sessionEnd);
          if (playbackTimer.current) clearInterval(playbackTimer.current);
        } else {
          setReplayPosition(newPos);
        }
      }, stepMs);
    } else {
      setIsReplaying(false);
      if (playbackTimer.current) clearInterval(playbackTimer.current);
    }
  }

  // Collect anomaly markers
  const anomalyMarkers = anomalies
    .filter((a) => a.timestamp_ms >= sessionStart && a.timestamp_ms <= sessionEnd)
    .map((a) => ({
      id: a.anomaly_id,
      pos: (a.timestamp_ms - sessionStart) / duration,
    }));

  function formatMs(ms: number): string {
    const diff = ms - sessionStart;
    const totalSeconds = Math.floor(diff / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  return (
    <div className="replay-scrubber">
      <div className="replay-controls">
        <button className="replay-btn replay-btn--play" onClick={handlePlay}>
          {isReplaying ? '⏸' : '▶'}
        </button>
        <div className="replay-speeds">
          {([1, 2, 5] as const).map((s) => (
            <button
              key={s}
              className={`replay-speed ${replaySpeed === s ? 'active' : ''}`}
              onClick={() => setReplaySpeed(s)}
            >
              {s}x
            </button>
          ))}
        </div>
        <span className="replay-time-label">
          {formatMs(replayPosition)} / {formatMs(sessionEnd)}
        </span>
      </div>

      <div className="replay-track-wrapper">
        <div
          className="replay-track"
          ref={trackRef}
          onMouseDown={handleMouseDown}
        >
          {/* Filled progress */}
          <div
            className="replay-track-fill"
            style={{ width: `${position * 100}%` }}
          />

          {/* Anomaly markers */}
          {anomalyMarkers.map((marker) => (
            <div
              key={marker.id}
              className="replay-anomaly-marker"
              style={{ left: `${marker.pos * 100}%` }}
              title="Anomaly"
              onClick={(e) => {
                e.stopPropagation();
                setReplayPosition(sessionStart + marker.pos * duration);
              }}
            />
          ))}

          {/* Playhead */}
          <div
            className="replay-playhead"
            style={{ left: `${position * 100}%` }}
          />
        </div>
      </div>

      <div className="replay-tick-labels">
        <span>0:00</span>
        <span>{formatMs(sessionStart + duration * 0.25)}</span>
        <span>{formatMs(sessionStart + duration * 0.5)}</span>
        <span>{formatMs(sessionStart + duration * 0.75)}</span>
        <span>{formatMs(sessionEnd)}</span>
      </div>
    </div>
  );
}
